/**
 * Seeded, deterministic pseudo-random number generation for the rules engine.
 *
 * Pure functions only: no I/O, no clock, no framework dependencies. The same
 * seed always produces the same sequence, which is what makes deals and
 * shuffles reproducible from a stored `RulesSnapshot.seed`.
 */

/**
 * Create a seeded PRNG returning floats in [0, 1).
 *
 * Implements mulberry32: a 32-bit state advanced by a Weyl-style additive
 * step and mixed with `Math.imul`-based avalanche rounds. Every `number` seed
 * is accepted and normalized to uint32 (`seed >>> 0`), so the sequence
 * depends only on the seed value mod 2^32. Deterministic and side-effect
 * free; calling the returned function repeatedly yields the stream.
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return function next(): number {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 0x100000000;
  };
}

/**
 * Fisher-Yates shuffle driven by `mulberry32(seed)`.
 *
 * Returns a NEW array; the input is never mutated. Deterministic: the same
 * seed applied to the same input always produces the same output order.
 * Iterates `i` from length-1 down to 1, chooses `j` uniformly from [0, i]
 * via `Math.floor(random() * (i + 1))`, and swaps positions `i` and `j`.
 */
export function shuffle<T>(array: readonly T[], seed: number): T[] {
  const result = Array.from(array);
  const random = mulberry32(seed);
  for (let i = result.length - 1; i >= 1; i--) {
    const j = Math.floor(random() * (i + 1));
    const swapped = result[i];
    result[i] = result[j];
    result[j] = swapped;
  }
  return result;
}

/**
 * Hash a string seed into an unsigned 32-bit integer (FNV-1a).
 *
 * Convenience helper for deriving a `mulberry32` seed from a string such as
 * a match id: `mulberry32(hashSeed(matchId))`. Deterministic and stable: the
 * same string always hashes to the same value. The empty string hashes to
 * the FNV-1a offset basis (0x811c9dc5).
 */
export function hashSeed(input: string): number {
  let hash = 0x811c9dc5 | 0;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}
